import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ExternalLink, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  draft: 'outline',
  in_progress: 'secondary',
  completed: 'default',
  conditional_approval: 'secondary',
  signed_off: 'default',
};

/**
 * Every check filled in against this piece of kit — the weekly forklift
 * sheets, lift inspections, pre-use checks. The link is made on the
 * checklist itself, so adding a new type of check later needs nothing here.
 */
const AssetChecklists = ({ asset, navigateTo, onClose }) => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/assets/${asset.id}/checklists`);
      setDocs(r.data);
    } catch { setDocs([]); }
    setLoading(false);
  }, [asset.id]);

  useEffect(() => { load(); }, [load]);

  const openDoc = (d) => {
    onClose?.();
    navigateTo?.('iso9001', { openChecklistId: d.id, isoTab: 'checklists' });
  };

  const statusLabel = (s) => t(`inv.checklists.status.${s}`, { defaultValue: (s || '').replace(/_/g, ' ') });

  return (
    <div className="space-y-3" data-testid="asset-checklists">
      <div>
        <h4 className="text-sm font-semibold">{t('inv.checklists.title', { count: docs.length })}</h4>
        <p className="text-xs text-muted-foreground">{t('inv.checklists.hint')}</p>
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : docs.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="no-asset-checklists">
          {t('inv.checklists.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[130px]">{t('inv.checklists.number')}</TableHead>
              <TableHead>{t('inv.checklists.document')}</TableHead>
              <TableHead className="hidden sm:table-cell w-[150px]">{t('common.status')}</TableHead>
              <TableHead className="hidden md:table-cell w-[110px]">{t('common.updated')}</TableHead>
              <TableHead className="w-[90px] text-right">{t('inv.checklists.open')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`asset-checklist-row-${d.checklist_number || d.id}`}>
                <TableCell className="font-mono text-xs">{d.checklist_number || '—'}</TableCell>
                <TableCell className="break-words">
                  <span className="text-sm font-medium">{d.template_name}</span>
                  <span className="block text-xs text-muted-foreground">{d.doc_ref}</span>
                  <span className="sm:hidden mt-0.5 block text-xs text-muted-foreground">
                    {statusLabel(d.status)}
                  </span>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge variant={STATUS_TONE[d.status] || 'outline'}>{statusLabel(d.status)}</Badge>
                </TableCell>
                <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                  {String(d.updated_at || d.created_at || '').slice(0, 10)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t('inv.checklists.downloadPdf')} asChild>
                      <a href={`${API}/checklists/instances/${d.id}/pdf`} target="_blank" rel="noreferrer"
                        data-testid={`asset-checklist-pdf-${d.id}`}>
                        <FileText className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                    {navigateTo && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" title={t('inv.checklists.openChecklist')}
                        onClick={() => openDoc(d)} data-testid={`asset-checklist-open-${d.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AssetChecklists;
